import { SearchRegistry } from '../types';

export const fleetManagementSearchEntries: SearchRegistry = {
  '/fleet/my-fleet/driver-management': {
    keywords: [
      'drivers',
      'driver management',
      'fleet drivers',
      'add driver',
      'remove driver',
      'driver list',
      'driver onboarding',
      'fahrer',
      'fahrerverwaltung',
      'fleet administration',
      'driver roster',
      'driver directory',
      'csv upload drivers',
      'add drivers via csv',
      'fleet manager',
      'fleet admin',
      'deactivate driver',
      'reactivate driver',
      'filter drivers',
      'search driver by email',
      'find driver',
    ],
    questions: [
      'How do I add a new driver to my fleet?',
      'Where do I see the list of drivers?',
      'How do I deactivate a driver?',
      'How do I assign a fuel card to a driver?',
      'How do I bulk-upload drivers from a CSV file?',
    ],
    faqs: [
      {
        q: 'How do I add a new driver to my fleet?',
        a: 'Open Fleet Management → My Fleet → Drivers. Use Add Driver, fill in the driver details (name, email, role) and save. The driver receives a welcome email with login instructions.',
      },
      {
        q: 'How do I block a driver who left the company?',
        a: 'Find the driver in Fleet Management → My Fleet → Drivers, open the detail view and use Deactivate. Active sessions are revoked and the driver can no longer authenticate.',
      },
      {
        q: 'How do I find one driver in a long list?',
        a: 'Use the Driver filter above the table and start typing — it searches the whole fleet on the server by first name, last name or email, and loads more results as you scroll. Type one name at a time rather than the full name.',
      },
      {
        q: 'Can I add many drivers at once?',
        a: 'Yes — use Add Drivers via CSV under My Fleet to upload a CSV. Once submitted you can monitor the progress on the Upload Session Statistics page.',
      },
    ],
  },
  '/fleet/my-fleet/vehicles': {
    keywords: [
      'vehicles',
      'vehicle management',
      'fleet vehicles',
      'add vehicle',
      'edit vehicle',
      'remove vehicle',
      'license plate',
      'fuel type',
      'vin',
      'battery capacity',
      'odometer',
      'fahrzeuge',
      'fahrzeugverwaltung',
      'kennzeichen',
      'kraftstoffart',
      'electric vehicle',
      'plug-in hybrid',
      'car list',
    ],
    questions: [
      'How do I add a vehicle to my fleet?',
      'Where do I see all vehicles of my fleet?',
      'How do I change the license plate of a vehicle?',
      'How do I remove a vehicle I no longer operate?',
      'Which fuel types can I choose for a vehicle?',
    ],
    faqs: [
      {
        q: 'How do I add a vehicle to my fleet?',
        a: 'Open Fleet Management → My Fleet → Vehicles and use Add vehicle. Only the license plate and the fuel type are required; VIN, brand, model, odometer and battery capacity can be added later.',
      },
      {
        q: 'Why is the battery capacity field disabled?',
        a: 'Battery capacity only applies to electric and plug-in hybrid vehicles. Select the matching fuel type first and the field becomes editable.',
      },
      {
        q: 'What happens when I remove a vehicle?',
        a: 'The vehicle leaves the list but stays in the history, so past transactions and invoices keep resolving. Its drivers are unassigned at the same time.',
      },
    ],
  },
  '/fleet/my-fleet/driver-login-access': {
    keywords: [
      'driver login',
      'driver access',
      'driver login access',
      'login configuration',
      'driver password',
      'driver email login',
      'sso',
      'login policy',
      'ryd email domain',
      'temp passwords',
      'temporary passwords',
      'temppasswort',
      'passwords card',
      'reset driver password',
      'fetch latest emails',
      'driver account id',
      'fleet temp password viewer',
      'important notes',
      'europe/berlin',
      'driver login emails',
    ],
    questions: [
      'How do I let drivers log into the fleet portal with their email?',
      'Where do I configure driver login access?',
      'How do I reset a driver password?',
      'How do I see the latest temp-password emails for a driver?',
    ],
    faqs: [
      {
        q: 'How do I enable email login for my drivers?',
        a: 'Open Fleet Management → My Fleet → Driver Login Access. The page is only available if your sponsor has a registered email domain. Once enabled, drivers can log in with their corporate email.',
      },
      {
        q: "How do I reset a driver's temporary password?",
        a: 'Filter by driver on Driver Login Access, then use the Reset Driver Password button on the relevant row. The Passwords card lists active temp-password records and timestamps; use Fetch latest emails to pull the most recent delivery confirmations.',
      },
    ],
  },
  '/fleet/my-fleet/order-management': {
    keywords: [
      'transactions',
      'orders',
      'order management',
      'fleet transactions',
      'fleet orders',
      'fuelling transactions',
      'history',
      'transaction history',
      'transaktionen',
      'tankvorgänge',
      'order list',
      'fleet administration',
      'time frame',
      'date range filter',
      'export transactions',
      'transaction export',
      'export to excel',
      'xlsx',
      'date range limit',
      'maximum time frame',
      'transaktionen exportieren',
      'export center',
    ],
    questions: [
      'Where do I see my fleet transactions?',
      'How do I export my transactions to Excel?',
      'Why is the Export button greyed out?',
      'How do I find a specific fuel transaction by date?',
      'How do I filter transactions by driver?',
    ],
    faqs: [
      {
        q: 'How do I export my fleet transactions?',
        a: 'Set the filters you need (date range, driver, vehicle) and use the Export button next to the Transactions heading — the export always matches the filters currently applied. It runs in the background: you get an in-app alert and an email when the Excel file is ready, and you download it from the Export Center.',
      },
      {
        q: 'Why is the Export button disabled?',
        a: 'Five reasons, and the page names the one that applies: no date range is selected (a range is required), the range is longer than six months, the current filters match no transactions, the period holds more than 100,000 records (choose a shorter time range), or you have already started this exact export. The button is only shown at all if your fleet is on a paid subscription plan.',
      },
      {
        q: 'Why does the transactions page look different for ryd internal users vs. fleet partners?',
        a: 'The page renders one of two views depending on your user group: the ryd-internal view (broader filters and partner picker) for users in the `ryd` group, and the fleet-partner view (scoped to your own sponsor) for everyone else.',
      },
    ],
  },
  '/fleet/my-fleet/configurations': {
    keywords: [
      'fleet configurations',
      'fleet configuration',
      'fleet settings',
      'sponsor configuration',
      'feature configuration',
      'limits',
      'purchase limits',
      'fleet card limits',
      'fleet preferences',
      'sponsor settings',
    ],
    questions: [
      'Where do I configure my fleet settings?',
      'How do I change my fleet purchase limits?',
    ],
  },
  '/fleet/savings-calculator': {
    keywords: [
      'savings calculator',
      'savings',
      'cost calculator',
      'fuel savings',
      'roi calculator',
      'cost comparison',
      'einsparungsrechner',
      'einsparungen',
      'partner savings calculator',
      'live config',
      'engine version',
      'engine outdated',
      'recalculate with latest',
      'human readable version',
      'metadata card',
      'partner setup',
      'calc name',
      'calculation tags',
      'status draft',
      'status final',
      'new calculation',
    ],
    questions: [
      'How much can my fleet save with ryd?',
      'How do I run a savings calculation for my fleet?',
      'What is the ROI of switching to ryd?',
      'My calculation says the engine is outdated — what does that mean?',
    ],
    faqs: [
      {
        q: 'How does the savings calculator work?',
        a: 'Open Fleet Management → Savings Calculator → Calculator and enter your fleet size, monthly fuel volume and current price terms. The calculator estimates the savings against ryd terms and shows the breakdown.',
      },
      {
        q: 'What does the "engine version outdated" banner mean?',
        a: 'A saved calculation stores the engine version it was computed with. When the live engine moves ahead, an alert at the top of the calculator offers a Recalculate button — running it re-evaluates the inputs against the latest pricing engine without losing your saved metadata.',
      },
      {
        q: 'What is the difference between a Draft and a Final calculation?',
        a: 'The Status pill at the top of the metadata card shows whether the calculation is Draft (still editable, intermediate state) or Final (locked-in version used for partner discussions). Switch between the two with the status control in the partner setup card.',
      },
    ],
  },
  '/fleet/savings-calculator/saved': {
    keywords: [
      'saved calculations',
      'saved savings',
      'savings history',
      'calculator history',
      'past calculations',
      'partner savings calculator list',
      'saved calc list',
      'reopen calculation',
    ],
    questions: [
      'Where do I find my previously saved savings calculations?',
      'How do I open a calculation I saved earlier?',
      'How do I duplicate a previous savings calculation?',
    ],
  },
  '/fleet/export-center': {
    keywords: [
      'export center',
      'data export',
      'csv export',
      'export transactions',
      'export invoices',
      'bulk export',
      'datenexport',
      'datev export',
      'invoice merge',
      'merge pdf',
      'merge csv',
      'merge excel',
      'export job',
      'export status',
      'export pending',
      'export processing',
      'export completed',
      'export failed',
      'export expired',
      'created date range',
      'data period',
      'transaction export',
      'transactions xlsx',
      'transactions excel',
      'excel export',
      'transaktionen exportieren',
      'transaktionsexport',
      'xlsx',
    ],
    questions: [
      'How do I export my fleet data in bulk?',
      'Where can I find the export center?',
      'How do I schedule a recurring export?',
      'Which formats are supported by the export center?',
      'What does the EXPIRED status mean for an export?',
      'How do I merge several invoices into a single PDF?',
      'Where do I download the transactions export I started?',
    ],
    faqs: [
      {
        q: 'What data and file types does the Export Center support?',
        a: 'The Export Center exposes the Invoice Merge category with three export types — Merge PDF, Merge CSV and Merge Excel. Pick the data period, file type and time range; completed jobs are downloadable from the same table.',
      },
      {
        q: 'My export shows status EXPIRED — what should I do?',
        a: 'Completed exports are kept for a limited time and then move to EXPIRED. Re-run the same job (same filters, same period) to regenerate the file; the new job will appear at the top of the list once it finishes processing.',
      },
      {
        q: 'How do I export my transactions, and where does the file appear?',
        a: 'Start the export from the Transactions page — the Export button next to the heading uses the filters currently applied there. The file is produced in the background as an Excel workbook; you get an in-app alert and an email when it is ready, and you download it from this page. The Transactions page accepts a period of up to six months, and a single export covers at most 100,000 records — narrow the time range if the button tells you the period is too large.',
      },
      {
        q: 'What do the export statuses (PENDING, PROCESSING, COMPLETED, FAILED, EXPIRED) mean?',
        a: 'PENDING = queued; PROCESSING = the merge is running; COMPLETED = the file is ready to download; FAILED = the job aborted (open the row for the error message); EXPIRED = the file has been removed and the job needs to be re-run.',
      },
    ],
  },
};
